import { languageOptions, defaultLanguage } from "../configs/languages";

/**
 * All supported language codes, including the default (English).
 */
export const SUPPORTED_LANGUAGES = languageOptions.map((option) => option.value);

/**
 * Supported language codes that get a URL prefix (everything but the default).
 */
export const NON_DEFAULT_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (lang) => lang !== defaultLanguage
);

/**
 * Maps any detected language code to one of our supported codes, so a
 * browser/region variant never leaks into URLs. Exact matches pass through
 * (en, pt-BR, ...); region variants collapse to their base (en-US -> en,
 * es-ES -> es, pt-PT -> pt-BR); anything unsupported falls back to the
 * default. Without this, i18next keeps the raw navigator language (e.g.
 * "en-US") and LocaleLink would emit junk /en-US/... URLs that Google crawls.
 * @param {string} lang - Any language code (possibly region-qualified).
 * @returns {string} A supported language code.
 */
export const normalizeLanguage = (lang) => {
  if (!lang) return defaultLanguage;
  if (SUPPORTED_LANGUAGES.includes(lang)) return lang;
  const base = lang.split("-")[0].toLowerCase();
  const match = SUPPORTED_LANGUAGES.find(
    (l) => l.toLowerCase() === base || l.split("-")[0].toLowerCase() === base
  );
  return match || defaultLanguage;
};

/**
 * Builds the locale-prefixed version of a base path.
 * The default language has no prefix; every other language is prefixed
 * with its code, e.g. localizePath("/pricing", "es") -> "/es/pricing".
 * The language is normalized first, so unsupported/region codes (en-US, ...)
 * never produce a prefix.
 * @param {string} basePath - Unprefixed path, e.g. "/pricing" or "/".
 * @param {string} lang - Language code.
 * @returns {string} The locale-prefixed path.
 */
export const localizePath = (basePath, lang) => {
  const normalized = normalizeLanguage(lang);
  const normalizedBase = basePath === "/" ? "" : basePath;
  if (normalized === defaultLanguage) return normalizedBase || "/";
  return `/${normalized}${normalizedBase}`;
};

/**
 * Splits a pathname into its language (if any) and base path.
 * @param {string} pathname - e.g. "/es/pricing" or "/pricing".
 * @returns {{ lang: string, basePath: string }}
 */
export const stripLocalePrefix = (pathname) => {
  const segments = pathname.split("/");
  const firstSegment = segments[1] || "";

  if (NON_DEFAULT_LANGUAGES.includes(firstSegment)) {
    const rest = "/" + segments.slice(2).join("/");
    return { lang: firstSegment, basePath: rest === "/" ? "/" : rest.replace(/\/$/, "") || "/" };
  }

  return { lang: defaultLanguage, basePath: pathname || "/" };
};
